// src/pages/CompaniesPage/index.jsx
import React from "react";
import {
    Box,
    Button,
    Alert,
    Backdrop,
    CircularProgress,
    Paper,
    TableContainer,
    TextField,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Divider,
} from "@mui/material";

import PageContainer from "../layout/PageContainer";
import useCompanies from "../../hooks/useCompanies";
import useCategories from "../../hooks/useCategories";
import useAmenities from "../../hooks/useAmenities";
import useClassifications from "../../hooks/useClassifications";
import useHotelGroups from "../../hooks/useHotelGroups";
import useAwards from "../../hooks/useAwards";
import useLogos from "../../hooks/useLogos";
import useCuisines from "../../hooks/useCuisines";
import useRestaurantPrices from "../../hooks/useRestaurantPrices";
import useDeliveryServices from "../../hooks/useDeliveryServices";
import useLocations from "../../hooks/useLocations";
import useLeisureActivities from "../../hooks/useLeisureActivities";
import useRegions from "../../hooks/useRegions";
import { useSearchParams } from "react-router-dom";

import CompaniesTable from "./CompaniesTable";
import CompanyDialog from "./CompaniesDialog";

import { companyMatchesSearch, stableSort, getComparator } from "./utils/companyUtils";
import { openCompanyScreenshotPrint } from "./utils/companyPrintUtils";

export default function CompaniesPage() {
    const {
        companies,
        loading,
        error,
        fetchCompanies,
        createCompany,
        updateCompany,
        deleteCompany,
        deleteCompanyImage,
        setCompanyStartImage,
        updateCompanyImageCrop,
        setCompanyActive,
        getCompanyById,
        getCompanyStats,
        getCompanyHistory,
        acquireCompanyEditLock,
        refreshCompanyEditLock,
        releaseCompanyEditLock,
        sortCompanyImages,
        uploadCompanyMenu,
        deleteCompanyMenu,
        setCompanyMenuUrl,
    } = useCompanies();

    const {
        categories,
        loading: categoriesLoading,
        error: categoriesError,
        fetchCategories,
    } = useCategories();

    const {
        amenities,
        loading: amenitiesLoading,
        error: amenitiesError,
        fetchAmenities,
    } = useAmenities();

    const {
        classifications,
        loading: classificationsLoading,
        error: classificationsError,
        fetchClassifications,
    } = useClassifications();

    const {
        hotelGroups,
        loading: hotelGroupsLoading,
        error: hotelGroupsError,
        fetchHotelGroups,
    } = useHotelGroups();

    const {
        awards,
        loading: awardsLoading,
        error: awardsError,
        fetchAwards,
    } = useAwards();

    const {
        logos,
        loading: logosLoading,
        error: logosError,
        fetchLogos,
    } = useLogos();

    const {
        cuisines,
        loading: cuisinesLoading,
        error: cuisinesError,
        fetchCuisines,
    } = useCuisines();

    const {
        restaurantPrices,
        loading: restaurantPricesLoading,
        error: restaurantPricesError,
        fetchRestaurantPrices,
    } = useRestaurantPrices();

    const {
        deliveryServices,
        loading: deliveryServicesLoading,
        error: deliveryServicesError,
        fetchDeliveryServices,
    } = useDeliveryServices();

    const {
        locations,
        loading: locationsLoading,
        error: locationsError,
        fetchLocations,
    } = useLocations();

    const {
        leisureActivities,
        loading: leisureActivitiesLoading,
        error: leisureActivitiesError,
        fetchLeisureActivities,
    } = useLeisureActivities();

    const {
        regions,
        loading: regionsLoading,
        error: regionsError,
        fetchRegions,
    } = useRegions();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("name");

    const [showInactive, setShowInactive] = React.useState(false);
    const [search, setSearch] = React.useState("");

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editingCompany, setEditingCompany] = React.useState(null);
    const [editOpeningCompany, setEditOpeningCompany] = React.useState(null);
    const [editLockToken, setEditLockToken] = React.useState("");
    const [lockWarningOpen, setLockWarningOpen] = React.useState(false);
    const [lockWarningCompany, setLockWarningCompany] = React.useState(null);
    const [lockWarningInfo, setLockWarningInfo] = React.useState(null);
    const [lockWarningError, setLockWarningError] = React.useState("");
    const [lockWarningLoading, setLockWarningLoading] = React.useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const [statsOpen, setStatsOpen] = React.useState(false);
    const [statsTarget, setStatsTarget] = React.useState(null);
    const [statsLoading, setStatsLoading] = React.useState(false);
    const [statsError, setStatsError] = React.useState("");
    const [statsData, setStatsData] = React.useState(null);

    const [historyOpen, setHistoryOpen] = React.useState(false);
    const [historyTarget, setHistoryTarget] = React.useState(null);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyError, setHistoryError] = React.useState("");
    const [historyRows, setHistoryRows] = React.useState([]);

    const loadDialogLookups = React.useCallback(() => {
        fetchCategories();
        fetchAmenities();
        fetchHotelGroups();
        fetchAwards();
        fetchLogos();
        fetchCuisines();
        fetchRestaurantPrices();
        fetchDeliveryServices();
        fetchLocations();
        fetchLeisureActivities();
        fetchRegions();
        fetchClassifications();
    }, [fetchCategories, fetchAmenities, fetchHotelGroups, fetchAwards, fetchLogos, fetchCuisines, fetchRestaurantPrices, fetchDeliveryServices, fetchLocations, fetchLeisureActivities, fetchRegions, fetchClassifications]);

    const createEditLockToken = React.useCallback(() => {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }, []);

    const openCompanyForEdit = React.useCallback((company, lockToken) => {
        setEditingCompany(company);
        setEditLockToken(lockToken);
        setLockWarningOpen(false);
        setLockWarningCompany(null);
        setLockWarningInfo(null);
        setLockWarningError("");
        setDialogOpen(true);
        loadDialogLookups();
    }, [loadDialogLookups]);

    const requestCompanyEdit = React.useCallback(async (company, force = false) => {
        if (!company?.id) return;

        const lockToken = createEditLockToken();
        setLockWarningError("");
        if (!force) setEditOpeningCompany(company);
        try {
            await acquireCompanyEditLock(company.id, lockToken, force);
            openCompanyForEdit(company, lockToken);
        } catch (e) {
            if (!force && e?.response?.status === 409) {
                setLockWarningCompany(company);
                setLockWarningInfo(e.response.data?.lock || null);
                setLockWarningOpen(true);
                return;
            }

            const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Bearbeitungsstatus konnte nicht geprüft werden.";
            if (force) {
                setLockWarningError(msg);
            } else {
                // eslint-disable-next-line no-alert
                alert(msg);
            }
        } finally {
            if (!force) setEditOpeningCompany(null);
        }
    }, [acquireCompanyEditLock, createEditLockToken, openCompanyForEdit]);

    const openCreate = () => {
        setEditingCompany(null);
        setEditLockToken("");
        setDialogOpen(true);
        loadDialogLookups();
    };

    const openEdit = (company) => {
        requestCompanyEdit(company, false);
    };

    const closeDialog = () => {
        const companyId = editingCompany?.id;
        const lockToken = editLockToken;
        setDialogOpen(false);
        setEditingCompany(null);
        setEditLockToken("");
        if (companyId && lockToken) {
            releaseCompanyEditLock(companyId, lockToken).catch(() => {});
        }
    };

    const confirmForceEdit = async () => {
        if (!lockWarningCompany?.id) return;
        setLockWarningLoading(true);
        setLockWarningError("");
        try {
            await requestCompanyEdit(lockWarningCompany, true);
        } finally {
            setLockWarningLoading(false);
        }
    };

    const askDelete = (company) => {
        setDeleteTarget(company);
        setDeleteError("");
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await deleteCompany(deleteTarget.id);
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
        } catch (e) {
            setDeleteError(e?.message || "Löschen fehlgeschlagen.");
        } finally {
            setDeleting(false);
        }
    };

    const filteredCompanies = React.useMemo(() => {
        const list = companies || [];
        const base = showInactive ? list : list.filter((c) => !!c.is_active);

        const s = search.trim();
        if (!s) return base;

        return base.filter((c) => companyMatchesSearch(c, s));
    }, [companies, showInactive, search]);

    const rows = React.useMemo(
        () => stableSort(filteredCompanies, getComparator(order, orderBy)),
        [filteredCompanies, order, orderBy]
    );

    const columns = React.useMemo(
        () => [
            { id: "is_active", label: "Aktiv" },
            { id: "customer_number", label: "Kundennr." },
            { id: "name", label: "Name" },
            { id: "slug", label: "Slug" },
            { id: "street", label: "Straße" },
            { id: "zip", label: "PLZ" },
            { id: "city", label: "Ort" },
            { id: "country", label: "Land" },
            { id: "lat", label: "Breite" },
            { id: "lon", label: "Länge" },
            { id: "page_views", label: "Seitenansichten" },
            { id: "last_change", label: "Letzte Änderung" },
        ],
        []
    );

    const handleToggleActiveInline = async (company) => {
        try {
            await setCompanyActive(company.id, !company.is_active);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(e?.message || "Status ändern fehlgeschlagen.");
        }
    };

    const openLiveView = (company) => {
        if (!company?.id) return;
        window.open(`/companies/${company.slug || company.id}`, "_blank", "noopener,noreferrer");
    };

    const printLiveView = (company) => {
        openCompanyScreenshotPrint(company);
    };

    const openStats = async (company) => {
        if (!company?.id) return;
        setStatsTarget(company);
        setStatsData(null);
        setStatsError("");
        setStatsOpen(true);
        setStatsLoading(true);
        try {
            const data = await getCompanyStats(company.id);
            setStatsData(data);
        } catch (e) {
            setStatsError(e?.response?.data?.error || e?.message || "Statistiken laden fehlgeschlagen.");
        } finally {
            setStatsLoading(false);
        }
    };

    const openHistory = async (company) => {
        if (!company?.id) return;
        setHistoryTarget(company);
        setHistoryRows([]);
        setHistoryError("");
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const data = await getCompanyHistory(company.id);
            setHistoryRows(Array.isArray(data?.history) ? data.history : []);
        } catch (e) {
            setHistoryError(e?.response?.data?.error || e?.message || "Änderungshistorie laden fehlgeschlagen.");
        } finally {
            setHistoryLoading(false);
        }
    };

    const [searchParams] = useSearchParams();

    React.useEffect(() => {
        const editId = searchParams.get("edit");

        if (!editId) return;

        const loadAndOpen = async () => {
            try {
                const company = await getCompanyById(editId);
                if (company) {
                    await requestCompanyEdit(company, false);
                }
            } catch (e) {
                console.error("Fehler beim Laden der Firma:", e);
            }
        };

        loadAndOpen();
    }, [searchParams, getCompanyById, requestCompanyEdit]);

    return (
        <PageContainer
            title="Firmen"
            actions={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                        }
                        label="Inaktive anzeigen"
                    />

                    <TextField
                        size="small"
                        placeholder="Suche… (Name, Ort, PLZ, Kundennr.)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 320 }}
                    />

                    <Button variant="outlined" onClick={() => fetchCompanies()} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neue Firma
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <CompaniesTable
                        rows={rows}
                        columns={columns}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={(colId) => {
                            const isAsc = orderBy === colId && order === "asc";
                            setOrder(isAsc ? "desc" : "asc");
                            setOrderBy(colId);
                        }}
                        onEdit={openEdit}
                        onDelete={askDelete}
                        onToggleActive={handleToggleActiveInline}
                        onOpenLive={openLiveView}
                        onPrintLive={printLiveView}
                        onOpenStats={openStats}
                        onOpenHistory={openHistory}
                    />
                )}
            </TableContainer>

            <Backdrop
                open={Boolean(editOpeningCompany)}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress color="inherit" />
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "rgba(0, 0, 0, 0.45)",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="subtitle2">
                        Bearbeitung wird vorbereitet
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {editOpeningCompany?.name || "Firmeneintrag"} wird geprüft.
                    </Typography>
                </Box>
            </Backdrop>

            <CompanyDialog
                open={dialogOpen}
                mode={editingCompany ? "edit" : "create"}
                initialCompany={editingCompany}
                categories={categories}
                categoriesLoading={categoriesLoading}
                categoriesError={categoriesError}
                amenities={amenities}
                amenitiesLoading={amenitiesLoading}
                amenitiesError={amenitiesError}
                hotelGroups={hotelGroups}
                hotelGroupsLoading={hotelGroupsLoading}
                hotelGroupsError={hotelGroupsError}
                awards={awards}
                awardsLoading={awardsLoading}
                awardsError={awardsError}
                logos={logos}
                logosLoading={logosLoading}
                logosError={logosError}
                cuisines={cuisines}
                cuisinesLoading={cuisinesLoading}
                cuisinesError={cuisinesError}
                restaurantPrices={restaurantPrices}
                restaurantPricesLoading={restaurantPricesLoading}
                restaurantPricesError={restaurantPricesError}
                deliveryServices={deliveryServices}
                deliveryServicesLoading={deliveryServicesLoading}
                deliveryServicesError={deliveryServicesError}
                locations={locations}
                locationsLoading={locationsLoading}
                locationsError={locationsError}
                leisureActivities={leisureActivities}
                leisureActivitiesLoading={leisureActivitiesLoading}
                leisureActivitiesError={leisureActivitiesError}
                regions={regions}
                regionsLoading={regionsLoading}
                regionsError={regionsError}
                classifications={classifications}
                classificationsLoading={classificationsLoading}
                classificationsError={classificationsError}
                editLockToken={editLockToken}
                onClose={closeDialog}
                api={{
                    getCompanyById,
                    refreshCompanyEditLock,
                    createCompany,
                    updateCompany,
                    deleteCompanyImage,
                    setCompanyStartImage,
                    updateCompanyImageCrop,
                    sortCompanyImages,
                    uploadCompanyMenu,
                    deleteCompanyMenu,
                    setCompanyMenuUrl,
                }}
            />

            <Dialog
                open={lockWarningOpen}
                onClose={() => !lockWarningLoading && setLockWarningOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Firma wird bereits bearbeitet</DialogTitle>
                <DialogContent dividers>
                    {lockWarningError ? (
                        <Alert severity="error">{lockWarningError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Der Firmeneintrag <b>{lockWarningCompany?.name}</b> wird aktuell
                            {lockWarningInfo?.username ? <> von <b>{lockWarningInfo.username}</b></> : " von einer anderen Sitzung"} bearbeitet.
                            Du kannst ihn trotzdem öffnen, überschreibst damit aber die bestehende Bearbeitungssperre.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLockWarningOpen(false)} disabled={lockWarningLoading}>
                        Abbrechen
                    </Button>
                    <Button variant="contained" color="warning" onClick={confirmForceEdit} disabled={lockWarningLoading}>
                        {lockWarningLoading ? "Wird geöffnet…" : "Ja, ok verstanden, trotzdem bearbeiten"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Firma löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du die Firma <b>{deleteTarget?.name}</b> wirklich löschen?
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Abbrechen
                    </Button>
                    <Button color="error" variant="contained" disabled={deleting} onClick={confirmDelete}>
                        {deleting ? "Löschen…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={historyOpen}
                onClose={() => !historyLoading && setHistoryOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Änderungshistorie {historyTarget?.name ? `- ${historyTarget.name}` : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {historyLoading ? (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : historyError ? (
                        <Alert severity="error">{historyError}</Alert>
                    ) : (
                        <CompanyHistoryTimeline rows={historyRows} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)} disabled={historyLoading}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    Statistiken {statsTarget?.name ? `- ${statsTarget.name}` : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {statsLoading ? (
                        <Box sx={{ py: 6, textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : statsError ? (
                        <Alert severity="error">{statsError}</Alert>
                    ) : (
                        <CompanyStatsPanel stats={statsData} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatsOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}

function CompanyHistoryTimeline({ rows }) {
    const list = Array.isArray(rows) ? rows : [];

    const formatDateTime = (value) => {
        if (!value) return "-";
        try {
            return new Intl.DateTimeFormat("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }).format(new Date(value));
        } catch {
            return value;
        }
    };

    if (!list.length) {
        return <Alert severity="info">Für diese Firma gibt es noch keine protokollierten Änderungen.</Alert>;
    }

    return (
        <Box sx={{ display: "grid" }}>
            {list.map((entry, index) => (
                <Box key={entry.id} sx={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 1.5 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main", mt: 0.75 }} />
                        {index < list.length - 1 && (
                            <Box sx={{ width: 2, flex: 1, minHeight: 36, bgcolor: "divider", mt: 0.75 }} />
                        )}
                    </Box>
                    <Box sx={{ pb: index < list.length - 1 ? 2 : 0 }}>
                        <Typography variant="subtitle2">
                            {entry.summary || entry.action || "Änderung"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {entry.username || "Unbekannt"} · {formatDateTime(entry.changed_at)}
                        </Typography>
                        {index < list.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

function CompanyStatsPanel({ stats }) {
    const daily = Array.isArray(stats?.daily) ? stats.daily : [];
    const recent = Array.isArray(stats?.recent) ? stats.recent : [];
    const maxViews = Math.max(1, ...daily.map((x) => Number(x.views) || 0));
    const chartWidth = Math.max(520, daily.length * 54);
    const chartHeight = 260;
    const padding = { top: 24, right: 18, bottom: 44, left: 42 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;
    const barGap = 12;
    const barWidth = daily.length ? Math.max(18, (plotWidth - barGap * (daily.length - 1)) / daily.length) : 0;

    const formatDate = (value) => {
        if (!value) return "-";
        try {
            return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" }).format(new Date(value));
        } catch {
            return value;
        }
    };

    const formatDateTime = (value) => {
        if (!value) return "-";
        try {
            return new Intl.DateTimeFormat("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }).format(new Date(value));
        } catch {
            return value;
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary">Seitenansichten gesamt</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats?.total ?? 0}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary">Tage mit Zugriffen</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{daily.length}</Typography>
                </Paper>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>Aufrufe nach Datum</Typography>
            <Paper variant="outlined" sx={{ p: 2, overflowX: "auto" }}>
                {daily.length ? (
                    <Box component="svg" width={chartWidth} height={chartHeight} sx={{ display: "block" }}>
                        <line
                            x1={padding.left}
                            y1={padding.top + plotHeight}
                            x2={chartWidth - padding.right}
                            y2={padding.top + plotHeight}
                            stroke="#d6d6d6"
                        />
                        <line
                            x1={padding.left}
                            y1={padding.top}
                            x2={padding.left}
                            y2={padding.top + plotHeight}
                            stroke="#d6d6d6"
                        />
                        {daily.map((item, index) => {
                            const views = Number(item.views) || 0;
                            const height = (views / maxViews) * plotHeight;
                            const x = padding.left + index * (barWidth + barGap);
                            const y = padding.top + plotHeight - height;

                            return (
                                <g key={`${item.date}-${index}`}>
                                    <rect x={x} y={y} width={barWidth} height={height} rx="4" fill="#f0ac4d" />
                                    <text x={x + barWidth / 2} y={Math.max(14, y - 7)} textAnchor="middle" fontSize="12" fill="#1a2538">
                                        {views}
                                    </text>
                                    <text x={x + barWidth / 2} y={chartHeight - 14} textAnchor="middle" fontSize="11" fill="#6b7280">
                                        {formatDate(item.date)}
                                    </text>
                                </g>
                            );
                        })}
                    </Box>
                ) : (
                    <Alert severity="info">Für diesen Eintrag wurden noch keine Seitenansichten erfasst.</Alert>
                )}
            </Paper>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Letzte Aufrufe</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                {recent.length ? (
                    <Box sx={{ display: "grid", gap: 0.75 }}>
                        {recent.slice(0, 10).map((item, index) => (
                            <Typography key={`${item}-${index}`} variant="body2">
                                {formatDateTime(item)}
                            </Typography>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">Noch keine Aufrufe vorhanden.</Typography>
                )}
            </Paper>
        </Box>
    );
}
